import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
import CataloguePicker from '../journalTransactions/CataloguePicker';
import useRefLists from '../rapports/useRefLists';
import canSeeFinance from '../../utils/canSeeFinance';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeImage,
  NdoeEmpty, NdoeLoading,
} from '../../components/ndoe';
import './commandeArticles.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const fmt = (v) => `${(Number(v) || 0).toLocaleString('fr-FR')} $`;

// Ajout d'articles à une commande — expérience CATALOGUE (M2, 2026-07-11).
// Remplace l'ancienne galerie muette (photos sans nom/prix/stock + fiche modale
// large) : la recherche visuelle du CataloguePicker porte tout — référence, prix,
// disponibilité, quantité — et chaque ajout apparaît aussitôt dans le
// récapitulatif de la commande, picker laissé ouvert pour enchaîner.
const Commande = () => {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  const navigate = useNavigate();
  const user = useSelector((s) => s.user?.currentUser);
  const financeVisible = canSeeFinance(user);

  const [commande, setCommande] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ajouts, setAjouts] = useState(0);
  const [ajoutEnCours, setAjoutEnCours] = useState(false);
  const refs = useRefLists(true);

  const load = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([
        axios.get(`${DOMAIN}/api/commande/commandeOne/${id}`),
        axios.get(`${DOMAIN}/api/commande/detail-commande/${id}`),
      ]);
      setCommande(Array.isArray(c.data) ? c.data[0] : c.data);
      setLignes(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      toast.error('Impossible de charger la commande.');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Prix envoyé = UNITAIRE (norme detail_commande — les vues multiplient par la
  // quantité). Même payload que Cart/PanierBar. Le picker reste ouvert
  // (multi-ajout) ; le 409 « déjà dans la commande » de l'API est affiché tel quel.
  const ajouter = async (v) => {
    if (ajoutEnCours) return;
    setAjoutEnCours(true);
    try {
      await axios.post(`${DOMAIN}/api/commande/detail-commande`, {
        id_commande: Number(id),
        id_varianteProduit: v.id_varianteProduit,
        id_taille: v.id_taille,
        // LE PRIX PROMO S'IL Y EN A UN (31/08/2026). On envoyait `v.prix`, le
        // prix de BASE, même sur une paire en promotion — et le serveur
        // l'acceptait, puisque le catalogue ET la promo sont deux prix de
        // référence valides (commandePrixGuard.js). Résultat mesuré en prod :
        // sur les 5 lignes créées depuis l'ouverture des 6 promotions actives,
        // 5 étaient au prix fort et 0 au prix promo. Les promotions existaient
        // à l'écran des promotions et nulle part ailleurs.
        // `??` et non `||` : un prix promo à 0 est un prix, pas une absence.
        prix: Number(v.prix_promo ?? v.prix) || 0,
        quantite: Number(v.quantite) || 1,
        user_cr: user?.id,
      });
      setAjouts((n) => n + 1);
      toast.success(`${v.code_article || v.nom_produit} · P${v.taille ?? '?'} — ${v.quantite || 1} paire(s) ajoutée(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Ajout impossible.');
    } finally { setAjoutEnCours(false); }
  };

  const total = lignes.reduce((s, l) => s + (Number(l.prix) || 0) * (Number(l.quantite) || 0), 0);
  const paires = lignes.reduce((s, l) => s + (Number(l.quantite) || 0), 0);

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow={`Commande #${id}`}
        title={commande?.nom || 'Ajouter des articles'}
        subtitle={[commande?.telephone, commande?.nom_commune].filter(Boolean).join(' · ')
          || 'Cherchez les paires dans la galerie — chaque ajout apparaît dans le récapitulatif.'}
        actions={(
          <>
            <NdoeActionButton variant="primary" onClick={() => setPickerOpen(true)}>
              🖼 Ajouter des articles
            </NdoeActionButton>
            {/* ⑧ Enchaînement : une fois les articles ajoutés, on file vers la
                page d'affectation (choix du livreur) au lieu de « Détail complet ». */}
            <NdoeActionButton variant="ink" onClick={() => navigate(`/listeDetailView/${id}`)}>
              Terminer et affecter →
            </NdoeActionButton>
          </>
        )}
      />

      {loading ? (
        <NdoeLoading rows={3} />
      ) : (
        <section className="ndoe-card ca-recap">
          <div className="ndoe-section__title">
            Articles de la commande — {paires} paire(s){financeVisible && total > 0 ? ` · ${fmt(total)}` : ''}
          </div>
          {!lignes.length ? (
            <NdoeEmpty
              icon="👟"
              title="Aucun article pour l'instant"
              text="Ouvrez la galerie pour ajouter les premières paires."
              action={(
                <NdoeActionButton variant="primary" onClick={() => setPickerOpen(true)}>
                  Ouvrir la galerie
                </NdoeActionButton>
              )}
            />
          ) : (
            <div className="ca-lignes">
              {lignes.map((l) => (
                <div className="ca-ligne" key={l.id_detail}>
                  <NdoeImage src={l.img} alt={l.nom_produit || 'Article'} size="md" />
                  <div className="ca-ligne__txt">
                    <b>{l.nom_produit || 'Article'}</b>
                    <small className="num">
                      {[l.code_article || l.code_produit, l.taille ? `P${l.taille}` : null].filter(Boolean).join(' · ')}
                    </small>
                  </div>
                  <div className="ca-ligne__chiffres num">
                    <b>{l.quantite} paire(s)</b>
                    {financeVisible && <small>{fmt((Number(l.prix) || 0) * (Number(l.quantite) || 0))}</small>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <CataloguePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        refLists={refs}
        avecPrix={financeVisible}
        avecQuantite
        bloquerRupture
        compteur={ajouts}
        onSelect={ajouter}
      />
    </NdoePage>
  );
};

export default Commande;
